/**
 * 検索付きプロパティ選択。
 *
 * プロパティ設定タブと、クライアントIDごとの送信設定（送付ファイルの上書き）の
 * 両方から使う。フォームを2種類作らないため共有している。
 */
import { useState, useRef, useEffect } from "react";
import { selectStyle } from "./ui.js";

export interface HubSpotProp {
  name: string;
  label: string;
  type: string;
  fieldType: string;
  /**
   * 選択式（enumeration）のときだけ入る選択肢。
   * **label は画面表示、value がレコードに入る内部値。**
   * 取引ステージのように両者が全く違うものがあるため、値で条件を書く画面では
   * 必ず value を保存する。
   */
  options?: Array<{ label: string; value: string }>;
}

export function PropSelect({ value, onChange, properties, filterType }: { value: string; onChange: (v: string) => void; properties: HubSpotProp[]; filterType?: string }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const filtered = (filterType ? properties.filter((p) => p.type === filterType || p.fieldType === filterType) : properties)
    .filter((p) => !search || p.label.toLowerCase().includes(search.toLowerCase()) || p.name.toLowerCase().includes(search.toLowerCase()));

  const selectedProp = properties.find((p) => p.name === value);
  const displayValue = selectedProp ? `${selectedProp.label}（${selectedProp.name}）` : "";

  // 外側クリックで閉じる
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          ...selectStyle, width: "100%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
          background: open ? "white" : "white", minHeight: 38,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: value ? "#1a1a1a" : "#999" }}>
          {displayValue || "-- 未設定 --"}
        </span>
        <span style={{ fontSize: 10, color: "#999", flexShrink: 0, marginLeft: 8 }}>▼</span>
      </div>

      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
          background: "white", border: "1px solid #e8e7e3", borderRadius: 8, marginTop: 4,
          boxShadow: "0 4px 16px rgba(0,0,0,0.1)", maxHeight: 280, display: "flex", flexDirection: "column",
        }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f0efeb" }}>
            <input
              type="text"
              placeholder="プロパティを検索..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              style={{ ...selectStyle, width: "100%", fontSize: 13, border: "1px solid #e8e7e3", padding: "6px 10px" }}
            />
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            <div
              onClick={() => { onChange(""); setOpen(false); setSearch(""); }}
              style={{ padding: "8px 12px", fontSize: 13, color: "#999", cursor: "pointer", borderBottom: "1px solid #f0efeb" }}
            >
              -- 未設定 --
            </div>
            {filtered.map((p) => (
              <div
                key={p.name}
                onClick={() => { onChange(p.name); setOpen(false); setSearch(""); }}
                style={{
                  padding: "8px 12px", fontSize: 13, cursor: "pointer",
                  background: p.name === value ? "#EEEDFE" : "white",
                  borderBottom: "1px solid #f8f7f5",
                }}
                onMouseEnter={(e) => { if (p.name !== value) (e.currentTarget.style.background = "#faf9f7"); }}
                onMouseLeave={(e) => { if (p.name !== value) (e.currentTarget.style.background = "white"); }}
              >
                <span style={{ fontWeight: 500 }}>{p.label}</span>
                <span style={{ color: "#999", marginLeft: 6, fontSize: 12 }}>({p.name})</span>
              </div>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: "12px", textAlign: "center", color: "#999", fontSize: 13 }}>該当なし</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

